import React, { useState } from 'react';
import { format, isSameDay, parseISO } from 'date-fns';
import { useCalendar } from '../../contexts/CalendarContext';
import { useNavigate } from 'react-router-dom';
import './Calendar.css';
// Import our custom event component
import { MonthEvent } from './EventComponent';

const MonthView = ({ onEventClick }) => {
  const { 
    currentDate, 
    selectedDate, 
    getDaysForMonthView, 
    onDateSelect, 
    events 
  } = useCalendar();
  
  const navigate = useNavigate();
  const [tooltipEvent, setTooltipEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  // Update day names to start with Monday
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const days = getDaysForMonthView();
  
  // Group days into weeks
  const weeks = [];
  let week = [];
  
  days.forEach((day, i) => {
    if (i % 7 === 0 && week.length) {
      weeks.push(week);
      week = [];
    }
    week.push(day);
    
    if (i === days.length - 1) {
      weeks.push(week);
    }
  });

  // Get events for a specific day based on start_date
  const getEventsForDay = (day) => {
    if (!events || !Array.isArray(events)) return [];
    
    return events.filter(event => {
      // Get the date from start_date field
      const eventStartDate = event.start_date ? parseISO(event.start_date) : 
                           (event.start ? parseISO(event.start) : null);
      
      // If no valid date, skip this event
      if (!eventStartDate) return false;
      
      // Check if the event's start_date matches this day
      return isSameDay(day.date, eventStartDate);
    });
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    
    // If provided, use the parent component's event handler
    if (onEventClick) {
      onEventClick(event);
    } else {
      // Default behavior - navigate to the booking details page
      navigate(`/admin/detail?id=${event.id}`);
    }
  };
  
  const showTooltip = (event, e) => {
    e.stopPropagation();
    
    // Set the position relative to the cursor
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY - 5,
      left: rect.left + window.scrollX + rect.width + 5
    });
    
    setTooltipEvent(event);
  };
  
  const hideTooltip = () => {
    setTooltipEvent(null);
  };
  
  // Add helper functions to handle complex fields in the tooltip
  const getDisplayValue = (value) => {
    if (!value) return 'N/A';
    
    if (typeof value === 'object') {
      // Handle different object structures
      if (value.label) return value.label;
      if (value.name) return value.name;
      if (value.value) return value.value;
      if (value.short) return value.short;
      if (value.text) return value.text;
      
      // If it's a date object
      if (value instanceof Date) return value.toLocaleDateString();
      
      // Try to convert to string as fallback
      return JSON.stringify(value);
    }
    
    return value;
  };

  // Helper to get location display
  const getLocationDisplay = (event) => {
    if (!event) return 'N/A';
    
    if (event.location) {
      if (typeof event.location === 'object') {
        return event.location.name || event.location.short || JSON.stringify(event.location);
      }
      return event.location;
    }
    
    if (event.location_name) return event.location_name;
    if (event.location_city) return event.location_city;
    
    return 'N/A';
  };

  // Helper to get studio display
  const getStudioDisplay = (event) => {
    if (!event) return 'N/A';
    
    if (event.studio) {
      if (typeof event.studio === 'object') {
        return event.studio.name || JSON.stringify(event.studio);
      }
      return event.studio;
    }
    
    return 'N/A';
  };

  // Helper for SonstigeInfo field
  const getSonstigeInfoDisplay = (event) => {
    if (!event) return 'N/A';
    
    // Check different possible property names for additional info
    if (event.SonstigeInfo) {
      if (typeof event.SonstigeInfo === 'object') {
        return event.SonstigeInfo.text || event.SonstigeInfo.value || '';
      }
      return event.SonstigeInfo;
    }
    
    // Check alternative property names
    if (event.sonstige_info) {
      if (typeof event.sonstige_info === 'object') {
        return event.sonstige_info.text || event.sonstige_info.value || '';
      }
      return event.sonstige_info;
    }
    
    return 'N/A';
  };

  return (
    <div className="calendar-month-view">
      
      <div className="calendar-days-header">
        {daysOfWeek.map((day, index) => (
          <div key={index} className="calendar-day-name">
            {day}
          </div>
        ))}
      </div>
      
      <div className="calendar-grid">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="calendar-week">
            {week.map((day, dayIndex) => {
              const dayEvents = getEventsForDay(day);
              
              return (
                <div 
                  key={dayIndex} 
                  className={`calendar-day ${!day.isCurrentMonth ? 'calendar-day-inactive' : ''} 
                              ${day.isToday ? 'calendar-day-today' : ''} 
                              ${isSameDay(day.date, selectedDate) ? 'calendar-day-selected' : ''}`}
                  onClick={() => onDateSelect(day.date)}
                >
                  <div className="calendar-day-number">
                    {format(day.date, 'd')}
                  </div>
                  
                  <div className="calendar-events">
                    {dayEvents.length > 0 && (
                      <div className="calendar-event-list">
                        {dayEvents.slice(0, 3).map(event => (
                          <MonthEvent 
                            key={event.id}
                            event={event}
                            onEventClick={onEventClick}
                          />
                        ))}
                        
                        {dayEvents.length > 3 && (
                          <div className="calendar-more-events">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      
      {tooltipEvent && (
        <div 
          className="calendar-event-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            maxWidth: '350px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px'
          }}
        >
          <div className="calendar-event-tooltip-title" style={{ gridColumn: '1 / span 2' }}>
            {tooltipEvent.title}
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Client: </span>
            <span className="calendar-tooltip-value">{getDisplayValue(tooltipEvent.client)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Talent: </span>
            <span className="calendar-tooltip-value">{getDisplayValue(tooltipEvent.talent_name || tooltipEvent.talent)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Position: </span>
            <span className="calendar-tooltip-value">{getDisplayValue(tooltipEvent.position)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Status: </span>
            <span className="calendar-tooltip-value">{tooltipEvent.status || 'N/A'}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Start: </span>
            <span className="calendar-tooltip-value">
              {tooltipEvent.start_date ? format(new Date(tooltipEvent.start_date), 'yyyy-MM-dd') : 'N/A'}
            </span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">End: </span>
            <span className="calendar-tooltip-value">
              {tooltipEvent.end_date ? format(new Date(tooltipEvent.end_date), 'yyyy-MM-dd') : 'N/A'}
            </span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Location: </span>
            <span className="calendar-tooltip-value">{getLocationDisplay(tooltipEvent)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Studio: </span>
            <span className="calendar-tooltip-value">{getStudioDisplay(tooltipEvent)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Time: </span>
            <span className="calendar-tooltip-value">
              {tooltipEvent.start_time || tooltipEvent.startTime || 'N/A'} - 
              {tooltipEvent.end_time || tooltipEvent.endTime || 'N/A'}
            </span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">KPI: </span>
            <span className="calendar-tooltip-value">{tooltipEvent.kpi || 'N/A'}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Set: </span>
            <span className="calendar-tooltip-value">{tooltipEvent.set || 'N/A'}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Option: </span>
            <span className="calendar-tooltip-value">{getDisplayValue(tooltipEvent.option)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Add. Info: </span>
            <span className="calendar-tooltip-value">{getSonstigeInfoDisplay(tooltipEvent)}</span>
          </div>
          
          <div className="calendar-tooltip-field">
            <span className="calendar-tooltip-label">Created By: </span>
            <span className="calendar-tooltip-value">{tooltipEvent.created_by || 'N/A'}</span>
          </div>
          
          {tooltipEvent.group && (
            <div className="calendar-tooltip-field" style={{ gridColumn: '1 / span 2' }}>
              <span className="calendar-tooltip-label">Group: </span>
              <span className="calendar-tooltip-value">
                <span style={{ 
                  backgroundColor: tooltipEvent.groupColor || '#888',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  color: '#fff'
                }}>
                  {tooltipEvent.group}
                </span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthView; 